// pages/CoursesPage.jsx
// Course Purchase Grid ("All tracks" section) — implements design handoff
// option 1b (Catalog cards) from the "Video course purchase page" design project.
// Card data (prices, module/lesson counts, enrolled) comes from the courses
// API (Courses/CourseAccess tables); the workbooks/reviews APIs still supply
// workbook counts for the sell threshold and the reviews marquee.

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { workbooksApi } from "../api/workbooks";
import { catalogueApi } from "../api/catalogue";
import { coursesApi } from "../api/courses";
import { enrolledFor } from "../data/courseStats";
import Navbar from "../components/Navbar";
import { EarlyBirdMiniBlock } from "../components/EarlyBirdCountdown";
import CourseSalesSections, {
  RefundPolicySection,
  FinalCtaSection,
} from "../components/course/CourseSalesSections";

// Design tokens from the handoff. Literal hexes, matching this codebase's
// page convention (see WorkbookCatalog.jsx).
const C = {
  cream: "#f2efe8",
  ink: "#1c1a17",
  muted: "#8a8478",
  border: "rgba(28,26,23,0.11)",
  border2: "rgba(28,26,23,0.22)",
  blue: "#2563eb",
  red: "#dc2626",
  gold: "#d97706",
  green: "#16a34a",
  brand: "#1e1b4b",
  text: "#18181b",
  textMuted: "#71717a",
  textSoft: "#a1a1aa",
  starEmpty: "#d4d4d8",
};

// Prerequisite step path: soft cream field, navy type, muted navy rule. The
// nodes take the card's accent, so these are the only fixed colors.
const PREREQ_BG = "#f5f2ec";
const PREREQ_LABEL = "#1a1a2e";
const PREREQ_HEADER = "rgba(26,26,46,0.58)";
const PREREQ_LINE = "rgba(26,26,46,0.34)";

// Semantic sets for includes-notes and discount pills.
const SEMANTIC = {
  red: { bg: "#fef2f2", border: "#fecaca", text: "#991b1b" },
  amber: { bg: "#fffbeb", border: "#fde68a", text: "#92400e" },
};


// Waitlist modal tokens: ink text on cream card, 16px rounded, no shadows.
const jakarta = "'Plus Jakarta Sans', sans-serif";
const WL_INK = "#1a1a2e";
const wlInputStyle = {
  fontFamily: jakarta,
  fontSize: 13,
  color: WL_INK,
  background: "#fff",
  border: "1px solid rgba(28,26,23,0.22)",
  borderRadius: 16,
  padding: "9px 14px",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};
const wlButtonStyle = {
  fontFamily: jakarta,
  fontSize: 13,
  fontWeight: 600,
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 16,
  padding: "10px 14px",
  cursor: "pointer",
};
const wlLabelStyle = {
  fontFamily: jakarta,
  fontSize: 11.5,
  fontWeight: 600,
  color: WL_INK,
};
// Required-field marker. Mobile stays unmarked (optional).
const WlReq = () => <span style={{ color: "#ea580c" }}> *</span>;

// Accent name (from the course record) → card palette. Presentation only;
// which accent a course uses is data.
const ACCENT_HEX = {
  blue: "#2563eb",
  red: "#dc2626",
  amber: "#d97706",
  green: "#16a34a",
};

const inr = (n) => "₹" + n.toLocaleString("en-IN");
const initialsOf = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";

export default function CoursesPage() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [workbooks, setWorkbooks] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [reviews, setReviews] = useState([]);
  // Waitlist modal for coming-soon tracks. wlTrack is the open card's
  // { slug, title }, null when closed.
  const [wlTrack, setWlTrack] = useState(null);
  const [wlName, setWlName] = useState("");
  const [wlEmail, setWlEmail] = useState("");
  const [wlMobile, setWlMobile] = useState("");
  const [wlStatus, setWlStatus] = useState("idle"); // idle | submitting | done | error
  const [wlMessage, setWlMessage] = useState("");

  useEffect(() => {
    let mounted = true;
    Promise.all([
      workbooksApi.getAll().catch(() => null),
      workbooksApi.getMarquee().catch(() => null),
      coursesApi.getAll().catch(() => null),
      catalogueApi.getBundles().catch(() => null),
    ]).then(([all, rev, courseList, bundleList]) => {
      if (!mounted) return;
      if (all) {
        setWorkbooks(all.workbooks ?? []);
      }
      if (bundleList) setBundles(bundleList);
      if (rev) {
        // Dedupe by id, keep natural order.
        const seen = new Set();
        setReviews(
          (rev.reviews ?? []).filter((r) => {
            if (r.id == null) return true;
            if (seen.has(r.id)) return false;
            seen.add(r.id);
            return true;
          }),
        );
      }
      if (courseList) setCourses(courseList);
    });
    return () => {
      mounted = false;
    };
  }, []);

  // The workbooks that come with a course, by the bundle the catalogue puts
  // them in. This used to re-derive membership from Track strings, a third copy
  // of a rule that lives on the bundle itself.
  const bundleForCourse = (course) =>
    bundles.find((b) => b.course && b.course.slug === course.slug) || null;

  const workbooksForTrack = (course) => {
    const bundle = bundleForCourse(course);
    if (!bundle) return [];
    return workbooks.filter((w) => w.bundleId === bundle.id);
  };

  // Enrolled is the course-native count from /api/courses (CourseAccess rows,
  // baseline included by the backend). Rating is hidden until a course-native
  // review source exists — workbook reviews don't describe the video course.
  const openWaitlist = (track) => setWlTrack({ slug: track.slug, title: track.title });
  const closeWaitlist = () => {
    setWlTrack(null);
    setWlName("");
    setWlEmail("");
    setWlMobile("");
    setWlStatus("idle");
    setWlMessage("");
  };

  // POST to /api/waitlist via the shared helper. Duplicate joins come back as
  // a 200 with their own message, so success handling covers both cases.
  const onJoinWaitlist = async () => {
    const name = wlName.trim();
    const email = wlEmail.trim();
    if (!name || !email || !email.includes("@")) {
      setWlStatus("error");
      setWlMessage("Please enter your name and a valid email.");
      return;
    }
    setWlStatus("submitting");
    setWlMessage("");
    try {
      const res = await coursesApi.joinWaitlist({
        name,
        email,
        mobile: wlMobile.trim() || null,
        courseSlug: wlTrack.slug,
        source: "courses-soon-card",
      });
      setWlStatus("done");
      setWlMessage(res?.message || "You are on the list.");
    } catch (e) {
      setWlStatus("error");
      setWlMessage(e.message || "Could not join the waitlist. Please try again.");
    }
  };

  const statsForTrack = (course) => ({
    rating: null,
    ratingCount: null,
    enrolled: enrolledFor(course),
  });

  // Course prices come from the catalogue, through /api/courses, in paise.
  // Price 0 means no active product sells this course yet: show no price rather
  // than invent one.
  const priceForTrack = (course) =>
    course.price > 0
      ? { current: course.price / 100, original: course.originalPrice / 100 }
      : null;

  const cards = courses.map((course) => {
    const count = workbooksForTrack(course).length;
    const { rating, ratingCount, enrolled } = statsForTrack(course);
    // Soon means the server says so: the course is ComingSoon, or no active
    // product sells it. The local "fewer than three workbooks" threshold this
    // page used to apply was a rule the backend never knew, so a course could
    // read as unavailable here and still be purchasable elsewhere.
    const soon = course.comingSoon || !course.product;
    const priceEntry = soon ? null : priceForTrack(course);
    const price = priceEntry ? priceEntry.current : null;
    const original = priceEntry ? priceEntry.original : null;
    const discount =
      price && original && original > price
        ? Math.round((1 - price / original) * 100)
        : 0;
    const meta =
      course.moduleCount > 0
        ? `${course.moduleCount} modules · ${course.lessonCount} lessons`
        : `${count} workbooks`;
    const accent = ACCENT_HEX[course.accent] || ACCENT_HEX.blue;
    // Tinted strip under the description. Live API tracks advertise the
    // bundled foundation. C# has no note now that the launch price expired.
    const note =
      !soon && course.includesFoundation
        ? "Buy this and get the full C# Foundation track free."
        : null;
    return {
      key: course.slug,
      accent,
      badge: course.badge,
      title: course.title,
      desc: course.description,
      note,
      includesNote: note ? (course.accent === "amber" ? "amber" : "red") : null,
      pill: course.accent === "amber" ? "amber" : "red",
      ctaColor: course.accent === "red" ? ACCENT_HEX.red : ACCENT_HEX.blue,
      to: `/courses/${course.slug}`,
      slug: course.slug,
      hasAccess: course.hasAccess === true,
      isCsharp: course.product === "csharp",
      soon, rating, ratingCount, enrolled, price, original, discount, meta,
    };
  });

  return (
    <div style={{ background: C.cream, minHeight: "100vh" }}>
      <Navbar />
      <CourseSalesSections />
      <div className="cpg-wrap">

        {/* Scroll target for the sales-section CTAs above. Zero-height anchor
            so it lands the viewport on the pricing block below without
            altering either existing block. */}
        <div id="tracks" aria-hidden="true" style={{ scrollMarginTop: 70 }} />

        {/* Section header */}
        <div className="cpg-head" style={{ borderTop: `2px solid ${C.ink}` }}>
          <div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: C.muted, marginBottom: 12 }}>
              All tracks
            </div>
            <h2 style={{ margin: 0, fontFamily: "'Playfair Display', Georgia, serif", fontSize: 44, fontWeight: 700, lineHeight: 1.1, color: C.ink }}>
              Pick your track.<br />
              <em style={{ fontStyle: "italic", color: C.blue }}>Ship a real tool.</em>
            </h2>
          </div>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 300, lineHeight: 1.8, color: C.muted, maxWidth: 340 }}>
clau            Browse and unlock more.
          </p>
        </div>

        {/* Track cards */}
        <div className="cpg-grid">
          {cards.map((t) => {
            const sem = SEMANTIC[t.pill];
            const noteSem = t.includesNote ? SEMANTIC[t.includesNote] : null;
            return (
              <div key={t.key} className="cpg-card">
                {/* Badge row */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: t.soon ? "space-between" : "flex-start", marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 9, height: 9, background: t.accent, borderRadius: 2, flexShrink: 0 }} />
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: t.accent }}>
                      {t.badge}
                    </span>
                  </div>
                  {t.soon && (
                    <span style={{ fontSize: 12, color: C.textMuted, border: `0.5px solid ${C.border2}`, padding: "4px 14px", borderRadius: 100 }}>
                      Soon
                    </span>
                  )}
                  {t.hasAccess && (
                    <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 700, color: C.green }}>
                      ✓ Owned
                    </span>
                  )}
                </div>

                {/* Title + description (muted when soon) */}
                <div style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 27, fontWeight: 700, lineHeight: 1.2, color: t.soon ? C.textSoft : C.text, marginBottom: 10 }}>
                  {t.title}
                </div>
                <p style={{ margin: `0 0 ${noteSem ? 14 : 16}px`, fontSize: 14, fontWeight: 300, lineHeight: 1.7, color: t.soon ? C.textSoft : C.textMuted }}>
                  {t.desc}
                </p>

                {/* API tracks build on the foundation track; C# is the entry
                    point and has no prerequisite of its own. The rule carries
                    the card's own accent so it reads as part of that track. */}
                {!t.isCsharp && (
                  <div className="cpg-prereq" style={{ marginBottom: noteSem ? 14 : 16 }}>
                    <div className="cpg-prereq-head">
                      C# Foundation recommended before this course
                    </div>
                    <div className="cpg-prereq-row">
                      <div className="cpg-prereq-step">
                        <span className="cpg-prereq-node cpg-prereq-node--done" style={{ background: t.accent, color: "#fff" }}>1</span>
                        <div>
                          <div className="cpg-prereq-name">C# Foundation</div>
                          <div className="cpg-prereq-state" style={{ color: t.accent }}>Recommended</div>
                        </div>
                      </div>
                      <span className="cpg-prereq-link" />
                      <div className="cpg-prereq-step">
                        <span className="cpg-prereq-node" style={{ border: `1.5px solid ${t.accent}`, color: PREREQ_LABEL }}>2</span>
                        <div>
                          <div className="cpg-prereq-name">This course</div>
                          <div className="cpg-prereq-state" style={{ color: C.textSoft }}>You're here</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {noteSem && !t.hasAccess && (
                  <div style={{ background: noteSem.bg, border: `0.5px solid ${noteSem.border}`, borderRadius: 8, padding: "9px 14px", fontSize: 12.5, color: noteSem.text, marginBottom: 16 }}>
                    ✓ {t.note}
                  </div>
                )}
                {t.isCsharp && !t.hasAccess && (
                  <EarlyBirdMiniBlock style={{ marginTop: noteSem ? -4 : 0, marginBottom: 16 }} />
                )}

                {t.soon ? (
                  <div style={{ marginTop: "auto", paddingTop: 16, borderTop: `0.5px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <button
                      onClick={() => openWaitlist(t)}
                      style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, color: C.blue, letterSpacing: "0.04em", background: "none", border: "none", padding: 0, cursor: "pointer" }}
                    >
                      Join the waitlist →
                    </button>
                    {/* The track is not purchasable yet, but its curriculum is
                        real and worth reading. Same destination as a live card. */}
                    <button
                      className="cpg-cta"
                      onClick={() => navigate(t.to)}
                      style={{ background: t.ctaColor }}
                    >
                      View track →
                    </button>
                  </div>
                ) : (
                  <div style={{ marginTop: "auto" }}>
                    {(t.rating != null || t.enrolled != null) && (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: C.text, marginBottom: 6 }}>
                        {t.rating != null && (
                          <>
                            <span style={{ color: C.gold }}>★</span>
                            <span style={{ fontWeight: 600 }}>{t.rating}</span>
                            <span style={{ color: C.textSoft }}>({t.ratingCount})</span>
                          </>
                        )}
                        {t.rating != null && t.enrolled != null && (
                          <span style={{ color: C.starEmpty }}>·</span>
                        )}
                        {t.enrolled != null && (
                          <span style={{ fontWeight: 500 }}>{t.enrolled} enrolled</span>
                        )}
                      </div>
                    )}
                    <div style={{ fontSize: 13, color: C.textMuted, paddingBottom: 16, borderBottom: `0.5px solid ${C.border}` }}>
                      {t.meta}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: t.hasAccess ? "flex-end" : "space-between", paddingTop: 16 }}>
                      {!t.hasAccess && (
                        <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
                          {t.price != null && (
                            <span style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 27, fontWeight: 700, color: C.text }}>
                              {inr(t.price)}
                            </span>
                          )}
                          {t.discount > 0 && (
                            <>
                              <span style={{ fontSize: 14, color: C.textSoft, textDecoration: "line-through" }}>
                                {inr(t.original)}
                              </span>
                              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, background: sem.bg, color: sem.text, padding: "3px 10px", borderRadius: 100 }}>
                                {t.discount}% off
                              </span>
                            </>
                          )}
                        </div>
                      )}
                      <button
                        className="cpg-cta"
                        onClick={() => navigate(t.hasAccess ? `/course/${t.slug}` : t.to)}
                        style={{ background: t.ctaColor }}
                      >
                        {t.hasAccess ? "Continue →" : "View track →"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* No Risk Refund Policy: right after the prices, so the guarantee
            answers the objection the moment it forms. */}
        <RefundPolicySection />

        {/* Trusted by engineers */}
        {reviews.length > 0 && (
          <section style={{ marginTop: 26 }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: C.gold, marginBottom: 16 }}>
              Trusted by engineers
            </div>
            <div className={reviews.length > 4 ? "cpg-reviews cpg-reviews--scroll" : "cpg-reviews"}>
              {reviews.map((r, i) => (
                <div key={r.id ?? i} className="cpg-review">
                  <div style={{ fontSize: 15, letterSpacing: 2, marginBottom: 12 }}>
                    <span style={{ color: C.gold }}>{"★".repeat(r.rating)}</span>
                    <span style={{ color: C.starEmpty }}>{"★".repeat(5 - r.rating)}</span>
                  </div>
                  {r.text && r.text.trim() !== "" ? (
                    <div style={{ fontSize: 14.5, color: C.text, marginBottom: 20 }}>{r.text}</div>
                  ) : (
                    <div style={{ fontSize: 14.5, fontStyle: "italic", color: C.textMuted, marginBottom: 20 }}>
                      Rated {r.rating} of 5
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: "auto" }}>
                    <div style={{ width: 38, height: 38, borderRadius: "50%", background: C.brand, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                      {initialsOf(r.name)}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{r.name}</div>
                      {r.role && <div style={{ fontSize: 12.5, color: C.textMuted }}>{r.role}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

      </div>

      {/* Full-bleed closing ask, below the pricing and social proof. */}
      <FinalCtaSection />

      {/* Waitlist modal for coming-soon tracks */}
      {wlTrack && (
        <div
          onClick={closeWaitlist}
          style={{ position: "fixed", inset: 0, background: "rgba(28,26,23,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 20 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#fffdf8", borderRadius: 16, padding: "26px 28px", width: "100%", maxWidth: 380, fontFamily: jakarta, color: WL_INK, position: "relative" }}
          >
            <button
              onClick={closeWaitlist}
              aria-label="Close"
              style={{ position: "absolute", top: 14, right: 16, background: "none", border: "none", fontSize: 18, color: C.textMuted, cursor: "pointer", lineHeight: 1 }}
            >
              ×
            </button>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Join the waitlist</div>
            <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 18 }}>{wlTrack.title}</div>
            {wlStatus === "done" ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ fontSize: 13.5, color: WL_INK, opacity: 0.75, lineHeight: 1.6 }}>{wlMessage}</div>
                <button onClick={closeWaitlist} style={wlButtonStyle}>
                  Close
                </button>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={wlLabelStyle}>
                    Your name
                    <WlReq />
                  </label>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={wlName}
                    onChange={(e) => setWlName(e.target.value)}
                    style={wlInputStyle}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={wlLabelStyle}>
                    Email
                    <WlReq />
                  </label>
                  <input
                    type="email"
                    placeholder="Email"
                    value={wlEmail}
                    onChange={(e) => setWlEmail(e.target.value)}
                    style={wlInputStyle}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={wlLabelStyle}>Mobile</label>
                  <PhoneInput
                    defaultCountry="IN"
                    international
                    placeholder="Mobile"
                    value={wlMobile || undefined}
                    onChange={(v) => setWlMobile(v || "")}
                    className="cpg-wl-phone"
                  />
                  <span style={{ fontFamily: jakarta, fontSize: 11.5, color: C.textMuted }}>
                    Optional. Add your number to get notified on WhatsApp.
                  </span>
                </div>
                <button
                  onClick={onJoinWaitlist}
                  disabled={wlStatus === "submitting"}
                  style={{ ...wlButtonStyle, cursor: wlStatus === "submitting" ? "default" : "pointer", opacity: wlStatus === "submitting" ? 0.7 : 1 }}
                >
                  {wlStatus === "submitting" ? "Joining..." : "Join waitlist"}
                </button>
                {wlStatus === "error" && (
                  <span style={{ fontSize: 11.5, color: "#b91c1c" }}>{wlMessage}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        .cpg-wrap{max-width:1160px;margin:0 auto;padding:56px 60px 64px;font-family:'DM Sans',system-ui,sans-serif;}
        .cpg-head{padding-top:28px;margin-bottom:44px;display:flex;justify-content:space-between;align-items:flex-end;gap:48px;}
        .cpg-grid{display:grid;grid-template-columns:1fr 1fr;gap:18px;}
        .cpg-card{background:#fff;border:0.5px solid ${C.border};border-radius:12px;padding:28px;display:flex;flex-direction:column;transition:border-color .2s;}
        .cpg-card:hover{border-color:rgba(28,26,23,0.4);}
        /* Locked-step band. Full-bleed inside the card, so it cancels the
           card's 28px padding and restates it as its own. */
        /* Locked-step panel: a contained card within the track card. */
        .cpg-prereq{margin:2px 0 0;padding:16px;background:${PREREQ_BG};border:0.5px solid ${C.border};border-radius:12px;}
        .cpg-prereq-head{font-family:'JetBrains Mono',monospace;font-size:9px;font-weight:700;letter-spacing:0.13em;text-transform:uppercase;line-height:1.5;color:${PREREQ_HEADER};margin-bottom:12px;}
        .cpg-prereq-row{display:flex;align-items:center;gap:10px;}
        .cpg-prereq-step{display:flex;align-items:center;gap:9px;min-width:0;}
        /* Numerals: mono digits sit high in the em box, so nudge to optical center. */
        .cpg-prereq-node{width:26px;height:26px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-family:'JetBrains Mono',monospace;font-size:11px;font-weight:700;line-height:1;padding-top:1px;box-sizing:border-box;}
        .cpg-prereq-node--done{transition:filter .2s;}
        .cpg-card:hover .cpg-prereq-node--done{filter:brightness(1.15);}
        .cpg-prereq-link{flex:1;min-width:18px;height:0;border-top:1.5px dotted ${PREREQ_LINE};}
        .cpg-prereq-name{font-family:'Plus Jakarta Sans',sans-serif;font-size:12.5px;font-weight:600;line-height:1.35;color:${PREREQ_LABEL};white-space:nowrap;}
        .cpg-prereq-state{font-family:'JetBrains Mono',monospace;font-size:8.5px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;line-height:1.4;margin-top:2px;white-space:nowrap;}
        .cpg-cta{font-family:'Space Mono',monospace;font-size:12px;letter-spacing:0.04em;color:#fff;border:none;padding:11px 20px;border-radius:8px;cursor:pointer;transition:opacity .2s;flex-shrink:0;}
        /* Waitlist modal phone input: match the modal's plain inputs. */
        .cpg-wl-phone .PhoneInputInput{font-family:'Plus Jakarta Sans',sans-serif;font-size:13px;color:#1a1a2e;background:#fff;border:1px solid rgba(28,26,23,0.22);border-radius:16px;padding:9px 14px;outline:none;width:100%;box-sizing:border-box;}
        .cpg-wl-phone .PhoneInputCountry{margin-right:8px;}
        .cpg-cta:hover{opacity:0.85;}
        .cpg-reviews{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;}
        .cpg-reviews--scroll{display:flex;overflow-x:auto;padding-bottom:8px;}
        .cpg-reviews--scroll .cpg-review{flex:0 0 264px;}
        .cpg-review{background:#fff;border:0.5px solid ${C.border};border-radius:12px;padding:22px 24px;display:flex;flex-direction:column;}
        @media (max-width:760px){
          .cpg-wrap{padding:40px 20px 56px;}
          .cpg-head{flex-direction:column;align-items:flex-start;gap:20px;}
          .cpg-grid{grid-template-columns:1fr;}
          .cpg-reviews{grid-template-columns:1fr 1fr;}
        }
        @media (max-width:520px){
          .cpg-reviews{grid-template-columns:1fr;}
        }
      `}</style>
    </div>
  );
}
